import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useParams, useHistory, Link } from "react-router-dom"; // v5
import { Avatar, Dropdown, Layout, Space, Typography, Tabs, Input, Button, Badge, Tooltip, List, Empty, Divider, Popconfirm } from "antd";
import { UserOutlined, SearchOutlined, DownloadOutlined, FileTextOutlined, MenuOutlined, BellOutlined, SettingOutlined, DeleteOutlined, CheckCircleOutlined, CloseCircleOutlined } from '@ant-design/icons';
import { CiLogout } from "react-icons/ci";
import { CgProfile } from "react-icons/cg";
import { motion } from "framer-motion";
import moment from "moment";
import ajaxService from "../services/ajax-service";
import fitMatchIcon from "../assets/icons/logo2.png";
import { calculateHeaderWidth, WIDTH_CONSTANTS } from "../utils/widthCalculator";

const { Header } = Layout;

const MainHeader = () => {
    let history = useHistory()
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchValue, setSearchValue] = useState('');
    const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
    const [headerWidth, setHeaderWidth] = useState(0);
    const [searchMaxWidth, setSearchMaxWidth] = useState(200);
    const [notifications, setNotifications] = useState([]);
    const [notificationVisible, setNotificationVisible] = useState(false);
    const [unreadCount, setUnreadCount] = useState(0);

    const fetchUser = useCallback(async (userId) => {
        setLoading(true);
        setError(null);
        try {
          const response = await ajaxService.get(`/users/1`);
          if (response?.success) {
            setUser(response.data || {});
          } else {
            setError(response?.message || "Failed to fetch user data.");
            setUser(null);
          }
        } catch (err) {
          setError("Error fetching user data.");
          setUser(null);
        } finally {
          setLoading(false);
        }
      }, []);

      useEffect(() => {
          fetchUser(1);
        },[]);

    // Fetch notifications
    const fetchNotifications = useCallback(async () => {
        try {
            // Replace with your actual notifications API endpoint
            const response = await ajaxService.get('/notifications');
            if (response?.success) {
                const notifs = response.data?.data || response.data || [];
                setNotifications(notifs);
                setUnreadCount(notifs.filter(n => !n.read).length);
            }
        } catch (error) {
            console.error('Error fetching notifications:', error);
            // Mock data for development
            setNotifications([
                { id: 1, title: 'New User Registration', message: 'A new user has registered', read: false, created_at: new Date().toISOString() },
                { id: 2, title: 'Payment Received', message: 'Payment of $50 received', read: false, created_at: new Date().toISOString() },
                { id: 3, title: 'Verification Pending', message: '3 users waiting for verification', read: true, created_at: new Date().toISOString() },
            ]);
            setUnreadCount(2);
        }
    }, []);

    useEffect(() => {
        fetchNotifications();
        // Poll for new notifications every 30 seconds
        const interval = setInterval(fetchNotifications, 30000);
        return () => clearInterval(interval);
    }, [fetchNotifications]);

    // Delete single notification
    const handleDeleteNotification = async (notificationId) => {
        try {
            const response = await ajaxService.delete(`/notifications/${notificationId}`);
            if (response?.success) {
                setNotifications(notifications.filter(n => n.id !== notificationId));
                setUnreadCount(prev => Math.max(0, prev - 1));
            }
        } catch (error) {
            console.error('Error deleting notification:', error);
            // Remove from local state even if API fails
            setNotifications(notifications.filter(n => n.id !== notificationId));
            setUnreadCount(prev => Math.max(0, prev - 1));
        }
    };

    // Delete all notifications
    const handleDeleteAllNotifications = async () => {
        try {
            const response = await ajaxService.delete('/notifications/all');
            if (response?.success) {
                setNotifications([]);
                setUnreadCount(0);
            }
        } catch (error) {
            console.error('Error deleting all notifications:', error);
            // Clear local state even if API fails
            setNotifications([]);
            setUnreadCount(0);
        }
    };

    // Mark all as read
    const handleMarkAllAsRead = async () => {
        try {
            const response = await ajaxService.post('/notifications/mark-all-read');
            if (response?.success) {
                setNotifications(notifications.map(n => ({ ...n, read: true })));
                setUnreadCount(0);
            }
        } catch (error) {
            console.error('Error marking all as read:', error);
            // Update local state even if API fails
            setNotifications(notifications.map(n => ({ ...n, read: true })));
            setUnreadCount(0);
        }
    };

    // Listen for sidebar collapse state and calculate header width
    useEffect(() => {
        const handleSidebarCollapse = (event) => {
            setSidebarCollapsed(event.detail.collapsed);
        };

        const updateHeaderWidth = () => {
            if (typeof window === 'undefined') return;
            
            const width = calculateHeaderWidth(sidebarCollapsed);
            setHeaderWidth(width);
            
            // Calculate responsive search max width based on available space
            // Reserve space for: logo (120px) + tabs (200px) + buttons (180px) + user menu (160px) + gaps (80px)
            // Total reserved: ~740px, so search can use remaining space
            const isMobile = window.innerWidth < 768;
            const isTablet = window.innerWidth < 1024 && window.innerWidth >= 768;
            
            // Adjust reserved space based on screen size
            let reservedSpace;
            let maxSearchWidth;
            
            if (isMobile) {
                // Mobile: logo (120px) + buttons (140px) + user (140px) + gaps (40px) = 440px
                reservedSpace = 440;
                maxSearchWidth = 0; // Hide search on mobile
            } else if (isTablet) {
                // Tablet: logo (120px) + tabs (180px) + buttons (160px) + user (150px) + gaps (50px) = 660px
                reservedSpace = 660;
                maxSearchWidth = 180;
            } else {
                // Desktop: logo (120px) + tabs (200px) + buttons (180px) + user (160px) + gaps (80px) = 740px
                reservedSpace = 740;
                maxSearchWidth = Math.min(300, Math.max(200, width - reservedSpace - 100));
            }
            
            const availableForSearch = Math.max(0, Math.min(width - reservedSpace, maxSearchWidth));
            setSearchMaxWidth(availableForSearch);
        };

        window.addEventListener('sidebarCollapse', handleSidebarCollapse);
        window.addEventListener('resize', updateHeaderWidth);
        
        // Initial calculation
        updateHeaderWidth();

        return () => {
            window.removeEventListener('sidebarCollapse', handleSidebarCollapse);
            window.removeEventListener('resize', updateHeaderWidth);
        };
    }, [sidebarCollapsed]);

    const avatarSrc = useMemo(() => {
        const src = user?.profile_image;
        return src && String(src).trim() ? src : undefined;
      }, [user?.profile_image]);

    const logout = () => {
        ajaxService.post('/logout')
        localStorage.removeItem('token');
        localStorage.removeItem('name');
        localStorage.removeItem('role');
        localStorage.removeItem('courtID');
        history.push({ pathname: '/' })
    }

    const userMenuItems = [
        {
            key: '1',
            label: <Link to="/profile">Profile</Link>,
            icon: <CgProfile />,
        },
        {
            type: 'divider',
        },
        {
            key: '2',
            label: 'Logout',
            icon: <CiLogout />,
            onClick: logout
        },
    ];

    const tabsItems = [
        {
            key: '1',
            label: 'Dashboard',
            navigate:"/"
        },
        {
            key: '2',
            label: 'Users',
            navigate:"/users"
        },
        {
            key: '3',
            label: 'Reports',
        },
    ];

    const handleExportData = () => {
        // Logic to export data as CSV
        const csvData = [
            ['Metric', 'Value'],
            ['Total Users', '37'],
            ['Total Revenue', '$550.00'],
            ['Pending Verifications', '1'],
            ['Questions', '8'],
            ['Lifestyle Questions', '10']
        ];

        const csvContent = csvData.map(row => row.join(',')).join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', 'dashboard-data.csv');
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const handleGenerateReport = () => {
        // Logic to generate PDF report using jsPDF and html2canvas
        import('jspdf').then(({ jsPDF }) => {
            import('html2canvas').then(({ default: html2canvas }) => {
                const dashboardElement = document.querySelector('.dashboard-content');
                if (dashboardElement) {
                    html2canvas(dashboardElement).then(canvas => {
                        const imgData = canvas.toDataURL('image/png');
                        const pdf = new jsPDF();
                        const imgWidth = 210;
                        const pageHeight = 295;
                        const imgHeight = (canvas.height * imgWidth) / canvas.width;
                        let heightLeft = imgHeight;

                        let position = 0;

                        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
                        heightLeft -= pageHeight;

                        while (heightLeft >= 0) {
                            position = heightLeft - imgHeight;
                            pdf.addPage();
                            pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
                            heightLeft -= pageHeight;
                        }

                        pdf.save('dashboard-report.pdf');
                    });
                }
            });
        });
    };

    const handleSearch = (value) => {
        setSearchValue(value);
        // Implement search logic here
        console.log('Searching for:', value);
    };

    return (
        <Header 
            className="main-header"
            style={{
            background: 'var(--dark-bg-start)',
            backdropFilter: 'blur(20px)',
                padding: '0 var(--spacing-lg)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            position: 'sticky',
            top: 0,
            zIndex: 50,
            borderBottom: '1px solid rgba(254, 2, 125, 0.2)',
            height: '64px',
                width: headerWidth > 0 ? `${headerWidth}px` : '100%',
                maxWidth: '100%',
                overflow: 'hidden',
                boxSizing: 'border-box',
                marginLeft: 0,
                gap: 'var(--spacing-md)',
                transition: 'width 0.3s ease-in-out'
        }}>
                {/* Left Section - Logo and Navigation */}
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--spacing-md)',
                    flexShrink: 1,
                    minWidth: 0,
                    flex: '0 1 auto',
                    minWidth: '200px' // Adjusted minimum width since logo is commented out
                }}>
                    {/* Logo - Commented out */}
                    {/* <motion.div
                        whileHover={{ scale: 1.02 }}
                        transition={{ type: "spring", stiffness: 300 }}
                        style={{ 
                            flexShrink: 0,
                            width: '120px',
                            height: '36px'
                        }}
                    >
                        <Link to="/" style={{ color: 'var(--text-gray-dark)', textDecoration: 'none', display: 'block' }}>
                            <img
                                src={fitMatchIcon}
                                alt="Tandemly"
                                style={{
                                    height: '36px',
                                    width: '120px',
                                    objectFit: 'contain',
                                    filter: 'brightness(1.1)',
                                    display: 'block'
                                }}
                            />
                        </Link>
                    </motion.div> */}

                    {/* Navigation Tabs */}
                    {/* <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        flexShrink: 1,
                        minWidth: 0,
                        overflow: 'hidden',
                        maxWidth: '250px'
                    }}>
                        <Tabs
                            defaultActiveKey="1"
                            items={tabsItems}
                            style={{
                                color: 'var(--text-gray-dark)',
                                fontWeight: 500,
                                minWidth: 0
                            }}
                            tabBarStyle={{
                                borderBottom: 'none',
                                color: 'var(--text-gray-dark)',
                                margin: 0,
                                padding: 0
                            }}
                            size="small"
                        />
                    </div> */}
            </div>

                {/* Right Section - Search, Actions, and User Menu */}
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--spacing-sm)',
                    flexShrink: 0,
                    minWidth: 0,
                    justifyContent: 'flex-end',
                    flex: '0 0 auto',
                    flexWrap: 'nowrap',
                    height: '100%'
                }}>
                    {/* Enhanced Search */}
                    {/* {searchMaxWidth > 0 && (
                    <motion.div
                        whileFocus={{ scale: 1.02 }}
                        transition={{ type: "spring", stiffness: 300 }}
                            style={{ 
                                flexShrink: 1, 
                                minWidth: 0,
                                width: `${searchMaxWidth}px`,
                                maxWidth: `${searchMaxWidth}px`,
                                flex: '0 1 auto',
                                display: 'flex',
                                alignItems: 'center',
                                height: '36px'
                            }}
                    >
                <Input.Search
                    placeholder="Search..."
                    style={{
                                    width: '100%',
                                    background: 'rgba(255, 255, 255, 0.1)',
                                    border: '1px solid rgba(254, 2, 125, 0.3)',
                                    borderRadius: 'var(--radius-md)',
                                    color: 'var(--text-white)',
                                    fontSize: '13px',
                                    height: '36px'
                    }}
                    prefix={
                        <SearchOutlined style={{
                                        color: 'var(--text-gray-light)',
                            fontSize: '14px'
                        }} />
                    }
                    value={searchValue}
                    onChange={(e) => setSearchValue(e.target.value)}
                    onSearch={handleSearch}
                            size="middle"
                                allowClear
                />
                    </motion.div>
                    )} */}

                    {/* Action Buttons - Export and Report only */}
                    {/* <div style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: 'var(--spacing-xs)',
                        flexShrink: 0,
                        height: '36px'
                    }}>
                        <Tooltip title="Export Data">
                            <motion.div 
                                whileHover={{ scale: 1.05 }} 
                                whileTap={{ scale: 0.95 }}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    height: '36px'
                                }}
                            >
                <Button
                    icon={<DownloadOutlined />}
                    onClick={handleExportData}
                    style={{
                                        background: 'var(--gradient-primary)',
                                        border: 'none',
                                        borderRadius: 'var(--radius-md)',
                                        height: '36px',
                                        padding: '0 14px',
                                        fontWeight: 500,
                                        fontSize: '12px',
                                        boxShadow: '0 2px 6px 0 rgba(254, 2, 125, 0.25)',
                                        whiteSpace: 'nowrap',
                                        color: 'var(--text-white)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                    }}
                                    size="small"
                                >
                                    Export
                </Button>
                            </motion.div>
                        </Tooltip>

                        <Tooltip title="Generate Report">
                            <motion.div 
                                whileHover={{ scale: 1.05 }} 
                                whileTap={{ scale: 0.95 }}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    height: '36px'
                                }}
                            >
                <Button
                    icon={<FileTextOutlined />}
                    onClick={handleGenerateReport}
                    style={{
                                        background: 'rgba(254, 2, 125, 0.15)',
                                        border: '1px solid rgba(254, 2, 125, 0.3)',
                                        borderRadius: 'var(--radius-md)',
                                        height: '36px',
                                        padding: '0 14px',
                                        fontWeight: 500,
                                        fontSize: '12px',
                                        whiteSpace: 'nowrap',
                                        color: 'var(--text-white)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                    }}
                                    size="small"
                                >
                                    Report
                </Button>
                            </motion.div>
                        </Tooltip>
                    </div> */}

                    {/* Notifications Dropdown - Now separate for uniform spacing */}
                    <Dropdown
                        overlay={
                            <div style={{
                                background: 'var(--dark-bg-card)',
                                borderRadius: 'var(--radius-lg)',
                                border: '1px solid rgba(254, 2, 125, 0.2)',
                                boxShadow: 'var(--shadow-lg)',
                                width: '380px',
                                maxHeight: '500px',
                                overflow: 'hidden',
                                display: 'flex',
                                flexDirection: 'column'
                            }}>
                                {/* Notification Header */}
                                <div style={{
                                    padding: 'var(--spacing-lg)',
                                    borderBottom: '1px solid rgba(254, 2, 125, 0.2)',
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    background: 'rgba(254, 2, 125, 0.05)'
                                }}>
                                    <Typography.Text strong style={{ color: 'var(--text-white)', fontSize: '1rem' }}>
                                        Notifications {unreadCount > 0 && `(${unreadCount})`}
                                    </Typography.Text>
                                    <Space>
                                        {notifications.length > 0 && (
                                            <>
                                                <Button
                                                    type="text"
                                                    size="small"
                                                    onClick={handleMarkAllAsRead}
                                                    style={{ color: 'var(--primary-purple)', fontSize: '12px' }}
                                                >
                                                    Mark all read
                                                </Button>
                                                <Popconfirm
                                                    title="Delete all notifications?"
                                                    onConfirm={handleDeleteAllNotifications}
                                                    okText="Yes"
                                                    cancelText="No"
                                                >
                                                    <Button
                                                        type="text"
                                                        size="small"
                                                        danger
                                                        icon={<DeleteOutlined />}
                                                        style={{ fontSize: '12px' }}
                                                    >
                                                        Clear all
                                                    </Button>
                                                </Popconfirm>
                                            </>
                                        )}
                                    </Space>
                                </div>

                                {/* Notifications List */}
                                <div style={{
                                    maxHeight: '400px',
                                    overflowY: 'auto',
                                    padding: 'var(--spacing-xs) 0'
                                }}>
                                    {notifications.length === 0 ? (
                                        <Empty
                                            image={Empty.PRESENTED_IMAGE_SIMPLE}
                                            description={<span style={{ color: 'var(--text-gray)' }}>No notifications</span>}
                                            style={{ padding: 'var(--spacing-2xl)' }}
                                        />
                                    ) : (
                                        <List
                                            dataSource={notifications}
                                            renderItem={(item) => (
                                                <List.Item
                                                    style={{
                                                        padding: 'var(--spacing-md) var(--spacing-lg)',
                                                        borderBottom: '1px solid rgba(254, 2, 125, 0.1)',
                                                        background: item.read ? 'transparent' : 'rgba(254, 2, 125, 0.05)',
                                                        cursor: 'pointer',
                                                        transition: 'var(--transition-fast)'
                                                    }}
                                                    onMouseEnter={(e) => {
                                                        e.currentTarget.style.background = 'rgba(254, 2, 125, 0.1)';
                                                    }}
                                                    onMouseLeave={(e) => {
                                                        e.currentTarget.style.background = item.read ? 'transparent' : 'rgba(254, 2, 125, 0.05)';
                                                    }}
                                                >
                                                    <List.Item.Meta
                                                        title={
                                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                                                <span style={{
                                                                    color: 'var(--text-white)',
                                                                    fontWeight: item.read ? 400 : 600,
                                                                    fontSize: '0.9rem'
                                                                }}>
                                                                    {item.title}
                                                                </span>
                                                                <Button
                                                                    type="text"
                                                                    size="small"
                                                                    danger
                                                                    icon={<DeleteOutlined style={{ fontSize: '12px' }} />}
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        handleDeleteNotification(item.id);
                                                                    }}
                                                                    style={{
                                                                        padding: '0 4px',
                                                                        height: '20px',
                                                                        minWidth: 'auto'
                                                                    }}
                                                                />
                                                            </div>
                                                        }
                                                        description={
                                                            <div>
                                                                <Typography.Text style={{
                                                                    color: 'var(--text-gray)',
                                                                    fontSize: '0.85rem',
                                                                    display: 'block',
                                                                    marginBottom: 'var(--spacing-xs)'
                                                                }}>
                                                                    {item.message}
                                                                </Typography.Text>
                                                                <Typography.Text style={{
                                                                    color: 'var(--text-gray-light)',
                                                                    fontSize: '0.75rem'
                                                                }}>
                                                                    {item.created_at ? moment(item.created_at).fromNow() : ''}
                                                                </Typography.Text>
                                                            </div>
                                                        }
                                                    />
                                                </List.Item>
                                            )}
                                        />
                                    )}
                                </div>
                            </div>
                        }
                        trigger={['click']}
                        placement="bottomRight"
                        visible={notificationVisible}
                        onVisibleChange={setNotificationVisible}
                    >
                        <motion.div 
                            whileHover={{ scale: 1.05 }} 
                            whileTap={{ scale: 0.95 }}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                height: '36px',
                                width: '36px'
                            }}
                        >
                            <Badge count={unreadCount} size="small" offset={[-2, 2]}>
                                    <Button
                                        icon={<BellOutlined />}
                                        shape="circle"
                                        style={{
                                        background: 'rgba(255, 255, 255, 0.1)',
                                        border: '1px solid rgba(254, 2, 125, 0.3)',
                                        color: 'var(--text-white)',
                                            width: '36px',
                                            height: '36px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        padding: 0
                                        }}
                                        size="small"
                                    />
                                </Badge>
                            </motion.div>
                    </Dropdown>

                    {/* User Menu */}
                    <Dropdown
                        menu={{ items: userMenuItems }}
                        placement="bottomRight"
                        trigger={['click']}
                    >
                        <motion.div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                cursor: 'pointer',
                                padding: 'var(--spacing-xs) var(--spacing-sm)',
                                borderRadius: 'var(--radius-md)',
                                transition: 'var(--transition-fast)',
                                flexShrink: 0,
                                marginLeft: 'var(--spacing-xs)',
                                border: '1px solid transparent',
                                gap: 'var(--spacing-sm)',
                                height: '36px'
                            }}
                            whileHover={{
                                backgroundColor: 'rgba(254, 2, 125, 0.1)',
                                borderColor: 'rgba(254, 2, 125, 0.3)',
                                scale: 1.02
                            }}
                            whileTap={{ scale: 0.98 }}
                        >
                            <Avatar
                                style={{
                                    background: 'var(--gradient-primary)',
                                    border: '2px solid rgba(254, 2, 125, 0.4)',
                                    flexShrink: 0,
                                    boxShadow: '0 2px 8px rgba(254, 2, 125, 0.2)',
                                    margin: 0,
                                    width: '36px',
                                    height: '36px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}
                                icon={<UserOutlined />}
                                src={avatarSrc}
                                size={36}
                            />
                            <div style={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'flex-start',
                                justifyContent: 'center',
                                minWidth: 0,
                                maxWidth: '120px',
                                overflow: 'hidden',
                                height: '36px',
                                gap: '2px'
                            }}>
                                <span style={{
                                    color: 'var(--text-white)',
                                    fontWeight: 600,
                                    fontSize: '0.9rem',
                                    lineHeight: 1.2,
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    display: 'block',
                                    width: '100%'
                                }}>
                                    {user?.name || 'Super Admin'}
                                </span>
                                <span style={{
                                    color: 'var(--text-gray-light)',
                                    fontSize: '0.75rem',
                                    whiteSpace: 'nowrap',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '4px',
                                    lineHeight: 1
                                }}>
                                    <span style={{
                                        width: '6px',
                                        height: '6px',
                                        borderRadius: '50%',
                                        background: 'var(--success-green)',
                                        display: 'inline-block',
                                        flexShrink: 0
                                    }}></span>
                                    Online
                                </span>
                            </div>
                        </motion.div>
                </Dropdown>
            </div>
        </Header>
    )
}

export default MainHeader